'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  LANGUAGE_MAP,
  SUPPORTED_LANGUAGES,
  getTranslations,
  type SupportedLocale,
  type Translations,
} from '@/lib/i18n/i18n';

interface LanguageContextValue {
  /** BCP-47 code, e.g. "pt" */
  languageCode: SupportedLocale;
  /** Full language name, e.g. "Portuguese" */
  language: string;
  /** Whether the user has explicitly chosen a language */
  hasChosenLanguage: boolean;
  /** Full translations object for the current locale */
  translations: Translations;
  /** Set language by code (e.g. "pt") */
  setLanguageByCode: (code: string) => void;
  /** Whether a translation is in progress (always false — kept for API compat) */
  isTranslating: boolean;
  /** Translate a map of UI strings — synchronous, no API call */
  t: (strings: Record<string, string>) => Promise<Record<string, string>>;
  /** Translate a single string — synchronous, no API call */
  tStr: (text: string) => Promise<string>;
}

const DEFAULT_CODE: SupportedLocale = 'en';
const DEFAULT_LANGUAGE = 'English';
const STORAGE_KEY = 'fanxiety_language';

const LanguageContext = createContext<LanguageContextValue>({
  languageCode: DEFAULT_CODE,
  language: DEFAULT_LANGUAGE,
  hasChosenLanguage: false,
  translations: getTranslations(DEFAULT_CODE),
  setLanguageByCode: () => {},
  isTranslating: false,
  t: async (s) => s,
  tStr: async (s) => s,
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  // Always start with English — identical on server and client first render
  const [languageCode, setLanguageCodeState] = useState<SupportedLocale>(DEFAULT_CODE);
  const [hasChosenLanguage, setHasChosenLanguage] = useState(false);
  const initialized = useRef(false);

  // Only read localStorage after mount — never during SSR
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const { code, chosen } = JSON.parse(stored);
        const supported = SUPPORTED_LANGUAGES.find((l) => l.code === code);
        if (supported) {
          setLanguageCodeState(supported.code);
          setHasChosenLanguage(chosen === true);
        }
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  const setLanguageByCode = useCallback((code: string) => {
    const supported = SUPPORTED_LANGUAGES.find((l) => l.code === code);
    const finalCode: SupportedLocale = supported ? supported.code : DEFAULT_CODE;
    setLanguageCodeState(finalCode);
    setHasChosenLanguage(true);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ code: finalCode, chosen: true }));
    } catch {
      // ignore storage errors
    }
  }, []);

  const translations = getTranslations(languageCode);
  const language = LANGUAGE_MAP[languageCode] ?? DEFAULT_LANGUAGE;

  /**
   * Translate a map of UI strings using static locale data.
   * Synchronous under the hood — returns a resolved Promise for API compatibility.
   */
  const t = useCallback(
    async (strings: Record<string, string>): Promise<Record<string, string>> => {
      if (languageCode === DEFAULT_CODE) return strings;
      const t = getTranslations(languageCode);
      const result: Record<string, string> = { ...strings };

      // Build a flat lookup: English value → translated value
      const enFlat = buildFlatLookup(getTranslations('en'));
      const tFlat = buildFlatLookup(t);

      for (const [key, value] of Object.entries(strings)) {
        // Find the i18n key for this English value
        const i18nKey = enFlat[value];
        if (i18nKey && tFlat[i18nKey] !== undefined) {
          result[key] = tFlat[i18nKey];
        }
      }
      return result;
    },
    [languageCode]
  );

  const tStr = useCallback(
    async (text: string): Promise<string> => {
      if (languageCode === DEFAULT_CODE) return text;
      const enFlat = buildFlatLookup(getTranslations('en'));
      const tFlat = buildFlatLookup(getTranslations(languageCode));
      const i18nKey = enFlat[text];
      if (i18nKey && tFlat[i18nKey] !== undefined) return tFlat[i18nKey];
      return text;
    },
    [languageCode]
  );

  return (
    <LanguageContext.Provider
      value={{
        languageCode,
        language,
        hasChosenLanguage,
        translations,
        setLanguageByCode,
        isTranslating: false,
        t,
        tStr,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Build a flat map: "value" → "section.key" and "section.key" → "value" */
function buildFlatLookup(translations: Record<string, any>): Record<string, string> {
  const flat: Record<string, string> = {};
  for (const [section, entries] of Object.entries(translations)) {
    if (typeof entries === 'object' && entries !== null) {
      for (const [key, value] of Object.entries(entries as Record<string, string>)) {
        const fullKey = `${section}.${key}`;
        flat[value as string] = fullKey; // English value → i18n key
        flat[fullKey] = value as string; // i18n key → translated value
      }
    }
  }
  return flat;
}
